/**
 * Pure H OS · Phase D Wave 2 · Documents.
 * Contracts, SOPs, policies, decks and templates.
 */
import { useMemo, useState, useEffect } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { UploadWidget, UploadPreview, UploadsList, UploadedRef } from "../components/UploadWidget";
import { DOCUMENTS, DocumentKind, DocumentStatus } from "../mocks/documentsData";
import { api } from "../services/api";

const KIND_LABEL: Record<DocumentKind, string> = {
  contract: "Contract", SOP: "SOP", policy: "Policy",
  deck: "Deck", template: "Template", financial: "Financial",
};
function statusSeverity(s: DocumentStatus): "low" | "medium" | "high" {
  if (s === "active") return "low";
  if (s === "draft" || s === "review") return "medium";
  return "high";
}

type Tab = "all" | DocumentKind;

export default function Documents() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadedRef[]>([]);
  const [preview, setPreview] = useState<UploadedRef | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<UploadedRef | null>(null);

  useEffect(() => {
    void (async () => {
      try { setUploads(await api.uploads() as UploadedRef[]); } catch { /* auth may fail during hot reload */ }
    })();
  }, []);

  async function refreshUploads() {
    try { setUploads(await api.uploads() as UploadedRef[]); } catch { /* ignore */ }
  }

  const filtered = useMemo(() => (tab === "all" ? DOCUMENTS : DOCUMENTS.filter((d) => d.kind === tab)), [tab]);
  const current = openId ? DOCUMENTS.find((d) => d.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <Card testId="doc-filters">
        <TabBar<Tab>
          testId="doc-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${DOCUMENTS.length}` },
            { key: "contract",  label: "Contracts" },
            { key: "SOP",       label: "SOPs" },
            { key: "policy",    label: "Policies" },
            { key: "deck",      label: "Decks" },
            { key: "template",  label: "Templates" },
            { key: "financial", label: "Financial" },
          ]}
        />
      </Card>

      <Card padded={false} testId="doc-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Document Vault" title={`${filtered.length} document${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Every contract, SOP, policy and deck across ORVAN.</div>
        </div>
        <div className="p-2">
          {filtered.map((d) => (
            <ListRow key={d.id} testId={`doc-${d.id}`}
              onClick={() => setOpenId(d.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{KIND_LABEL[d.kind]}</span>
                  <span>{d.title}</span>
                  {d.confidential && <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-danger)" }}>CONF</span>}
                </span>
              }
              sublabel={<>{d.owner} · {d.version} · updated {d.updatedRelative}</>}
              trailing={<SeverityBadge severity={statusSeverity(d.status)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="doc-drawer" title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${KIND_LABEL[current.kind]} · ${current.status}`} title={current.owner} />
            {current.body && (
              <div className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ph-text)" }} data-testid={`doc-body-${current.id}`}>
                {current.body}
              </div>
            )}

            <div className="pt-4" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>
                {replaceTarget ? `Upload new version of "${replaceTarget.name}"` : "Attach a file to this document"}
              </div>
              <UploadWidget
                linkedTo={`documents/${current.id}`}
                replacesId={replaceTarget?.id}
                onUploaded={() => { setReplaceTarget(null); void refreshUploads(); }}
                testId={`doc-upload-${current.id}`}
              />
              {replaceTarget && (
                <button className="text-xs mt-1" style={{ color: "var(--ph-muted)" }} onClick={() => setReplaceTarget(null)} data-testid={`doc-upload-cancel-${current.id}`}>
                  Cancel replace
                </button>
              )}
              <div className="mt-3">
                <UploadsList
                  items={uploads.filter((u) => u.linkedTo === `documents/${current.id}`)}
                  onOpen={setPreview}
                  onDeleted={() => void refreshUploads()}
                  onReplace={(u) => setReplaceTarget(u)}
                  testId={`doc-uploads-${current.id}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Version</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.version}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Updated</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.updatedRelative}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Confidential</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.confidential ? "Yes" : "No"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Tags</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.tags.join(", ")}</div>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <UploadPreview upload={preview} onClose={() => setPreview(null)} testId="doc-upload-preview" />
    </div>
  );
}
